import React, { useEffect, useMemo, useState, useRef } from 'react';
import Drawer from '../common/Drawer';
import SaveCancelButtons from '../common/buttons/SaveCancelButtons';
import RadioButtons from '../common/buttons/RadioButtons';
import { styled } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { showAlert } from '../../data/stores/alertStore';
import Autocomplete, { AutocompleteOption } from '../common/form/AutoComplete';
import Input from '../common/form/Input';
import MaskInput from '../common/form/MaskInput';
import Select from '../common/form/Select';
import { AppDispatch, RootState } from '../../data/store';
import { fetchGoogleUsers } from '../../services/company-service';
import {
	CrewRolesDDOptions,
	GoogleUser,
	UserDTO,
	UserPermissionsDDOptions,
} from '../../data/models/user';
import { createUsersFromGoogleUsers } from '../../data/thunks/userThunk';
import { closeModal } from '../../data/stores/modalStore';
import useUsersList from '../../hooks/useUsersList';
import { Button, Checkbox, FormControlLabel } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { createCompanyExternalUser } from '../../data/thunks/companyExternalThunk';
import { fetchUserGroups } from '../../data/thunks/userGroupThunk';
import { UserGroup } from '../../data/models/userGroup';
import { AsyncStage } from '../../data/reduxCommon';

type CrewType = 'INTERNAL' | 'EXTERNAL' | '';
type EmployeeStatus = 'EMPLOYEE' | 'NOT_EMPLOYEE' | '';

export default function AddCrewModal() {
	const { openId, props } = useSelector(
		(state: RootState) => state.modalState
	);
	const open = openId === 'userCreationModal';
	const initialCrewType = props?.initialCrewType as CrewType | undefined;

	const dispatch = useDispatch<AppDispatch>();
	const { list: users } = useUsersList(open);
	const { userGroups } = useSelector(
		(state: RootState) => state.userGroupState
	);
	const { stage } = useSelector(
		(state: RootState) => state.companyExternalUserState
	);
	const { company } = useSelector((state: RootState) => state.companyState);
	const isLoadingGroupsRef = useRef(false);

	const [googleUsers, setGoogleUsers] = useState<GoogleUser[]>([]);
	const [crewType, setCrewType] = useState<CrewType>('');
	const [employeeStatus, setEmployeeStatus] = useState<EmployeeStatus>('');
	const [selectedGoogleUser, setSelectedGoogleUser] =
		useState<AutocompleteOption | null>(null);
	const [selectedGroups, setSelectedGroups] = useState<UserGroup[]>([]);
	const [selectedRole, setSelectedRole] = useState<string>('');
	const [selectedPermission, setSelectedPermission] = useState<string>('');
	const [subscribed, setSubscribed] = useState<string>('');
	const [externalEmail, setExternalEmail] = useState<string>('');
	const [externalFirstName, setExternalFirstName] = useState<string>('');
	const [externalLastName, setExternalLastName] = useState<string>('');
	const [externalPhoneNumber, setExternalPhoneNumber] = useState<string>('');
	const [sendInvite, setSendInvite] = useState<boolean>(false);
	const [externalCustomMessage, setExternalCustomMessage] =
		useState<string>('');

	// Convert user groups to AutoComplete options format
	const groupOptions = useMemo(() => {
		return userGroups.list.map((group) => ({
			id: group.id,
			display: group.name,
		}));
	}, [userGroups.list]);

	// Convert selected groups to AutoComplete format
	const selectedGroupOptions = useMemo(() => {
		return selectedGroups.map((group) => ({
			id: group.id,
			display: group.name,
		}));
	}, [selectedGroups]);

	// Set initial crew type when modal opens with prop
	useEffect(() => {
		if (open && initialCrewType) {
			setCrewType(initialCrewType);
		}
	}, [open, initialCrewType]);

	useEffect(() => {
		const fetchGoogleUsersAsync = async () => {
			const response = await fetchGoogleUsers();
			setGoogleUsers(response);
		};
		if (open && crewType === 'INTERNAL') {
			fetchGoogleUsersAsync();
		}
	}, [open, crewType]);

	// Fetch user groups when modal opens
	useEffect(() => {
		if (
			open &&
			company?.id &&
			!userGroups.initialized &&
			!isLoadingGroupsRef.current
		) {
			isLoadingGroupsRef.current = true;
			dispatch(
				fetchUserGroups({ companyId: company.id, take: 1000 })
			).finally(() => {
				isLoadingGroupsRef.current = false;
			});
		}
	}, [open, company?.id, dispatch, userGroups.initialized]);

	// Auto-select "not an employee" when External Crew is selected
	useEffect(() => {
		if (crewType === 'EXTERNAL') {
			setEmployeeStatus('NOT_EMPLOYEE');
		}
	}, [crewType]);

	useEffect(() => {
		if (subscribed === 'UNSUBSCRIBED') {
			setSelectedPermission('');
		}
	}, [subscribed]);

	const userDropdownOptions = useMemo(() => {
		const usersIds = users.map((user) => user.googleId);
		const availableGoogleUsers = googleUsers.filter(
			(googleUser) => !usersIds.includes(googleUser.id)
		);

		return availableGoogleUsers.map((user) => ({
			id: user.id,
			display: user.primaryEmail,
		}));
	}, [googleUsers, users]);

	const handleGroupsChange = (event: any, newValue: any) => {
		// Convert AutoComplete options back to UserGroup objects
		const selectedGroupsData = newValue
			.map((option: any) => {
				return userGroups.list.find((group) => group.id === option.id);
			})
			.filter(Boolean);
		setSelectedGroups(selectedGroupsData);
	};

	const handleClose = () => {
		dispatch(closeModal());
		// Reset form state
		setCrewType('');
		setEmployeeStatus('');
		setSelectedGoogleUser(null);
		setSelectedGroups([]);
		setSelectedRole('');
		setSelectedPermission('');
		setExternalEmail('');
		setExternalFirstName('');
		setExternalLastName('');
		setExternalPhoneNumber('');
		setSubscribed('');
		setSendInvite(false);
		setExternalCustomMessage('');
	};

	const handleAdd = () => {
		if (crewType === 'INTERNAL') {
			if (!selectedGoogleUser) {
				dispatch(
					showAlert({
						message: 'Please select a Google user.',
						severity: 'error',
					})
				);
				return;
			}

			const googleUser = googleUsers.find(
				(u) => u.id === selectedGoogleUser.id
			);
			if (!googleUser) {
				dispatch(
					showAlert({
						message: 'An error occurred creating the user.',
						severity: 'error',
					})
				);
				return;
			}

			const userToCreate: UserDTO = {
				googleId: googleUser.id,
				email: googleUser.primaryEmail,
				firstName: googleUser.name.givenName,
				lastName: googleUser.name.familyName,
				role: selectedRole,
				permissions: selectedPermission,
				groupIds: selectedGroups.map((group) => group.id),
			};

			dispatch(createUsersFromGoogleUsers([userToCreate]));
		}
		handleClose();
	};

	const handleSaveOrInvite = async () => {
		await dispatch(
			createCompanyExternalUser({
				email: externalEmail,
				firstName: externalFirstName,
				lastName: externalLastName,
				phone: externalPhoneNumber,
				role: selectedRole,
				sendInvite,
				customMessage: sendInvite ? externalCustomMessage : undefined,
				groupIds: selectedGroups.map((group) => group.id),
			})
		);
		handleClose();
	};

	const isFormValid = () => {
		if (stage === AsyncStage.Busy) {
			return false;
		}

		if (crewType === 'INTERNAL') {
			return selectedGoogleUser !== null;
		} else {
			return (
				externalEmail.trim() !== '' &&
				externalEmail.includes('@') &&
				externalFirstName.trim() !== '' &&
				externalLastName.trim() !== ''
			);
		}
	};

	const typeOptions = [
		{ value: 'INTERNAL', label: 'Internal Staff' },
		{ value: 'EXTERNAL', label: 'External Crew' },
	];

	const employeeOptions = [
		{ value: 'EMPLOYEE', label: 'This person is an employee' },
		{ value: 'NOT_EMPLOYEE', label: 'This person is not an employee' },
	];

	const subscribedOptions = [
		{
			value: 'SUBSCRIBED',
			label: 'Subscribed (paid, has access to Orbito)',
		},
		{
			value: 'UNSUBSCRIBED',
			label: 'Unsubscribed (free, no access to Orbito)',
		},
	];

	return (
		<Drawer
			open={open}
			handleClose={handleClose}
			title={`Add New ${crewType === 'INTERNAL' ? 'Staff' : 'Crew'}`}
			width={500}
		>
			<FormContainer>
				<RadioButtons
					label="Type"
					name="person-type"
					options={typeOptions}
					value={crewType}
					onChange={(e) => setCrewType(e.target.value as CrewType)}
				/>

				{!!crewType && (
					<RadioButtons
						label="Employee"
						name="employee-status"
						options={employeeOptions}
						value={employeeStatus}
						onChange={(e) =>
							setEmployeeStatus(e.target.value as EmployeeStatus)
						}
						disabled={crewType === 'EXTERNAL'}
					/>
				)}

				{crewType === 'INTERNAL' && (
					<>
						<RadioButtons
							label="Orbito Access"
							name="subscribed"
							options={subscribedOptions}
							value={subscribed}
							onChange={(e) =>
								setSubscribed(e.target.value as string)
							}
						/>
					</>
				)}

				{crewType === 'INTERNAL' && (
					<>
						<Autocomplete
							id="google-user"
							label="Google User"
							value={selectedGoogleUser}
							options={userDropdownOptions}
							onChange={(e, value) => {
								setSelectedGoogleUser(value);
							}}
						/>

						<Select
							label="Permission"
							value={selectedPermission}
							options={UserPermissionsDDOptions}
							onChange={(event: SelectChangeEvent) =>
								setSelectedPermission(event.target.value)
							}
							disabled={subscribed === 'UNSUBSCRIBED'}
						/>

						<Select
							label="Role"
							value={selectedRole}
							options={CrewRolesDDOptions}
							onChange={(event: SelectChangeEvent) =>
								setSelectedRole(event.target.value)
							}
						/>

						<Autocomplete
							id="groups"
							label="Groups"
							value={selectedGroupOptions}
							options={groupOptions}
							multiple={true}
							placeholder="Select groups..."
							onChange={handleGroupsChange}
						/>
					</>
				)}

				{crewType === 'EXTERNAL' && (
					<>
						<ExternalNote>
							Enter the external crew member's details below to
							invite them to the Orbtio space, this person will
							only be able to access projects they are assigned
							to.
						</ExternalNote>

						<Input
							id="external-first-name"
							label="First Name"
							value={externalFirstName}
							onChange={(e) =>
								setExternalFirstName(e.target.value)
							}
							placeholder="John"
							required
						/>

						<Input
							id="external-last-name"
							label="Last Name"
							value={externalLastName}
							onChange={(e) =>
								setExternalLastName(e.target.value)
							}
							placeholder="Doe"
							required
						/>

						<Input
							id="external-email"
							label="Email"
							value={externalEmail}
							onChange={(e) => setExternalEmail(e.target.value)}
							type="email"
							placeholder="john.doe@email.com"
							required
						/>

						<MaskInput
							id="external-phone"
							name="externalPhoneNumber"
							label="Phone Number"
							value={externalPhoneNumber}
							onChange={(e) =>
								setExternalPhoneNumber(e.target.value)
							}
							mask="phone"
						/>

						<Select
							label="Role"
							value={selectedRole}
							options={CrewRolesDDOptions}
							onChange={(event: SelectChangeEvent) =>
								setSelectedRole(event.target.value)
							}
						/>

						<Autocomplete
							id="groups-external"
							label="Groups"
							value={selectedGroupOptions}
							options={groupOptions}
							multiple={true}
							placeholder="Select groups..."
							onChange={handleGroupsChange}
						/>

						{/* <FormControlLabel
							control={
								<Checkbox
									checked={sendInvite}
									onChange={(e) =>
										setSendInvite(e.target.checked)
									}
								/>
							}
							label="Send Invite"
						/> */}

						{sendInvite && (
							<Input
								id="external-custom-message"
								label="Custom Message (Optional)"
								value={externalCustomMessage}
								onChange={(e) =>
									setExternalCustomMessage(e.target.value)
								}
								multiline
								rows={3}
								placeholder="Enter a custom message to include with the invitation..."
							/>
						)}
					</>
				)}

				<ActionButtonsContainer>
					{crewType === 'INTERNAL' ? (
						<SaveCancelButtons
							handleSave={handleAdd}
							handleCancel={handleClose}
							saveText="CREATE"
							saveDisabled={!isFormValid()}
						/>
					) : (
						<ExternalActionButtons>
							<Button
								variant="outlined"
								onClick={handleClose}
								sx={{ mr: 2 }}
							>
								CANCEL
							</Button>
							<Button
								variant="contained"
								onClick={handleSaveOrInvite}
								disabled={!isFormValid()}
							>
								CREATE
							</Button>
						</ExternalActionButtons>
					)}
				</ActionButtonsContainer>
			</FormContainer>
		</Drawer>
	);
}

const FormContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${(props) => props.theme.spacing(2)};
`;

const ActionButtonsContainer = styled.div`
	margin-top: ${(props) => props.theme.spacing(4)};
	text-align: right;
`;

const ExternalActionButtons = styled.div`
	display: flex;
	justify-content: flex-end;
	align-items: center;
`;

const Note = styled.div`
	color: ${({ theme }) => theme.palette.text.secondary};
	font-size: 14px;
	line-height: 1.5;
`;

const ExternalNote = styled.div`
	color: ${({ theme }) => theme.palette.text.secondary};
	font-size: 14px;
	line-height: 1.5;
	margin-bottom: ${(props) => props.theme.spacing(2)};
`;
